import { useCallback, useMemo } from 'react';

import config from '@/config';

import {
  GeolocationResponse,
  useGeolocationCallback,
} from '../use-geolocation-callback';
import { PLATFORMS } from '@/utilities/constants/game';
import { DdEventCategory, DdEventType } from '@/utilities/logger/datadog/types';
import { Logger } from '@/utilities/logger/datadog/rumEvents/rumEvents';
import { GeolocationParams } from '../types';
import { useStore } from '@/store/useStore';
import { isIosWrapper } from '@/utilities/helpers/wrapperHelpers';

enum GeolocationCountry {
  USA = 'US',
  CANADA = 'CA',
}

export type GeolocationConfig = {
  serviceName: string;
  clientAuthToken: string;
  installerKey: string;
  env: string;
  country?: string;
};

const getEnvironment = () => {
  const { env, geocomplyEnv } = config;
  if (env === 'prod') {
    return { type: 'prod' as const };
  }
  if (geocomplyEnv) {
    return geocomplyEnv;
  }

  return { type: 'cert' as const };
};

export const useGeolocationHelpers = () => {
  const platform = useStore((s) => s.runtime.platform);
  const { geolocationCallback } = useGeolocationCallback();

  const {
    serviceName: appName,
    clientAuthToken,
    installerKey,
  } = config.geolocation as GeolocationConfig;

  const isCountryUSA = process.env?.COUNTRY === 'usa';

  const country = useMemo(
    () =>
      config?.geolocation?.country ??
      (isCountryUSA ? GeolocationCountry.USA : GeolocationCountry.CANADA),
    [isCountryUSA],
  );

  const configureGeolocation = useCallback(
    ({ region, product, ...session }: GeolocationParams, Geolocation) =>
      Geolocation.setConfig({
        appDomain: {
          product,
          region: region.toUpperCase() as Uppercase<string>,
        },
        appName,
        appVersion: process.env.BUILD_VERSION ?? 'local-development',
        clientAuthToken,
        installerKey,
        environment: getEnvironment(),
        country: country as GeolocationCountry,
        session,
      }),
    [country, appName, clientAuthToken, installerKey],
  );

  const locateUser = useCallback(
    async (
      params: GeolocationParams,
      domain: 'casino' | 'poker' = params.product,
    ) => {
      const mobileWrapperParams = [
        params.userId,
        params.userAuthToken,
        params.sessionId,
        config.geolocation.clientAuthToken,
      ] as const;

      if (isIosWrapper()) {
        Logger.logRumAction({
          name: '[GW][GEOLOCATION] Delegated to native app',
          useCase: DdEventCategory.Geolocation,
          eventType: DdEventType.Info,
        });
      }
      if (platform === PLATFORMS.Desktop || platform === PLATFORMS.MobileWeb) {
        const { Geolocation } = await import('@fanduel/geolocation-sdk');

        Geolocation.init(
          config.geolocation.serviceName,
          process.env.BUILD_VERSION ?? 'local-development',
        );
        Geolocation.setCallback((action) =>
          geolocationCallback(action as unknown as GeolocationResponse),
        );

        Logger.logRumAction({
          name: '[GW][GEOLOCATION] Triggered',
          useCase: DdEventCategory.Geolocation,
          eventType: DdEventType.Info,
        });
        configureGeolocation({ ...params, product: domain }, Geolocation).then(
          () => {
            Geolocation.locateUser('game launch');
          },
        );
      } else {
        if (
          window?.AndroidWrapper?.triggerLocateUserWithDomain ||
          window?.MobileWrapper?.triggerLocateUserWithDomain
        ) {
          window?.AndroidWrapper?.triggerLocateUserWithDomain?.(
            ...mobileWrapperParams,
            domain,
          );
          window?.MobileWrapper?.triggerLocateUserWithDomain?.(
            ...mobileWrapperParams,
            domain,
          );
        } else {
          window?.AndroidWrapper?.triggerLocateUser?.(...mobileWrapperParams);
          window?.MobileWrapper?.triggerLocateUser?.(...mobileWrapperParams);
        }
      }
    },
    [platform, geolocationCallback, configureGeolocation],
  );

  return { locateUser };
};
